
"use client";

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import moment, { defaultDateRange, startOfDay, endOfDay, isDateTimeInRange, parseAppDateTime, formatInAppTz } from '../utils/timezone';
import { useAuth } from '../context/AuthContext';
import { otherTipsService } from '../apis/otherTipoutService';
import { employeeManagementService } from '../apis/employeeManagement';
import DateRangePicker from '../components/ui/date-range-picker';
import OtherTipsAddModal from '../components/othertips-addmodal';
import OtherTipsEditModal from '../components/other-tips-editmodal';
import OtherTipsDeleteModal from '../components/other-tips-deletemodal';
import { OtherTipsTransactionTable } from '../components/other-tipstransaction-table';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EmployeeTotalsOthersTable } from '../components/employee-totals-otherstip';
import {
  calculateTipCommission,
  getTipRoleFromEmployeeRole,
} from '../utils/tipCommission';
import { base64Logo } from '../constants/pdfLogo';

const calculateCommission = (amount, employeeRole, transactionDate = new Date()) =>
  calculateTipCommission(amount, getTipRoleFromEmployeeRole(employeeRole), transactionDate);

export default function OtherTipsRecords() {
    const { currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [allemployees,setallemployees]=useState([]);

    // Modal states
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);

    // Filter states
    const [dateRange, setDateRange] = useState(defaultDateRange);

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    // Balance data state
    const [balanceData, setBalanceData] = useState({
        tipPool: 0,
        collectedFees: 0,
        addedTokes: 0,
    });


    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await employeeManagementService.getAllEmployees({ limit: 1000000 });
                 
                    if (response.success) {
                    setallemployees(response?.data?.body?.data?.employees);
                }
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        };

        fetchEmployees();
    }, []);

    // Fetch balance data
    const fetchBalanceData = useCallback(async (startDate = null, endDate = null) => {
        try {
            const params = {};

            // Add date parameters if provided
            if (startDate && endDate) {
                params.startDate = moment.tz(startDate, "America/Chicago").format("YYYY-MM-DD");
                params.endDate = moment.tz(endDate, "America/Chicago").format("YYYY-MM-DD");
            }

            const response = await otherTipsService.getBalances(currentUser?.role, params);

            if (response.success) {
                setBalanceData({
                    tipPool: response.data?.tipPool || 0,
                    collectedFees: response.data?.collectedFees || 0,
                    addedTokes: response.data?.addedTokes || 0,
                });
            } else {
                console.error("Failed to load balance data:", response.error);
                toast.error("Failed to load balance data");
            }
        } catch (err) {
            console.error("Error fetching balance data:", err);
            toast.error("Failed to load balance data");
        }
    }, [currentUser]);

    // Updated useEffect to pass date range to fetchBalanceData
    useEffect(() => {
        if (currentUser) {
            fetchBalanceData(dateRange.start, dateRange.end);
        }
    }, [dateRange, fetchBalanceData, currentUser]);

    // Fetch records with date filtering
    const fetchRecords = useCallback(async (startDate = null, endDate = null) => {
        const userStr = JSON.parse(localStorage.getItem('user'));

        try {
            setIsLoading(true);
            const params = {};

            if (startDate && endDate) {
                params.startDate = moment.tz(startDate, "America/Chicago").format("YYYY-MM-DD");
                params.endDate = moment.tz(endDate, "America/Chicago").format("YYYY-MM-DD");
            }

            const response = await otherTipsService.getTipHistory(userStr?.role, params.startDate, params.endDate);

            if (response.success && Array.isArray(response.data)) {
                setRecords(response.data);
            } else {
                console.error("Invalid records response:", response);
                setRecords([]);
            }
        } catch (err) {
            console.error("Error fetching records:", err);
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecords(dateRange.start, dateRange.end);
    }, [dateRange, fetchRecords]);

    useEffect(() => {
        if (currentUser) {
            fetchBalanceData(dateRange.start, dateRange.end);
        }
    }, [dateRange, fetchBalanceData, currentUser]);

    // Initial data fetch - removed refreshFlag dependency and made it depend on other functions
    const fetchInitialData = useCallback(async () => {
        try {
            setIsLoading(true);
            await Promise.all([
                fetchBalanceData(dateRange.start, dateRange.end),
                fetchRecords(dateRange.start, dateRange.end)
            ]);
        } catch (err) {
            console.error("Error fetching initial data:", err);
            setError("Failed to load data");
            toast.error("Failed to load other tips data");
        } finally {
            setIsLoading(false);
        }
    }, [fetchBalanceData, fetchRecords, dateRange]);

    // Initial data fetch - now includes fetchInitialData in dependencies
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData, refreshFlag]);

    // Handle adding a new record
    const handleAddRecord = async (data) => {
        try {
            setIsLoading(true);
            const response = await otherTipsService.addRecord({
                ...data,
                added_by: currentUser.username
            });

            if (response.success) {
                toast.success("Record added successfully");
                // Refresh data after adding
                await Promise.all([
                    fetchBalanceData(dateRange.start, dateRange.end),
                    fetchRecords(dateRange.start, dateRange.end)
                ]);
                setRefreshFlag(prev => !prev);
                setShowAddModal(false);
            } else {
                toast.error(response.error || "Failed to add record");
            }
        } catch (err) {
            console.error("Error adding record:", err);
            toast.error("An error occurred while adding record");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle record update
    const handleUpdateRecord = async (id, data) => {
        try {
            setIsLoading(true);
            const response = await otherTipsService.updateRecord(id, {
                ...data,
                date: parseAppDateTime(data.date)?.format("YYYY-MM-DDTHH:mm:ss") ?? data.date,
                added_by: currentUser.username
            });

            if (response.success) {
                toast.success("Record updated successfully");
                // Refresh data after updating
                await Promise.all([
                    fetchBalanceData(dateRange.start, dateRange.end),
                    fetchRecords(dateRange.start, dateRange.end)
                ]);
                setRefreshFlag(prev => !prev);
                setShowEditModal(false);
            } else {
                toast.error(response.error || "Failed to update record");
            }
        } catch (err) {
            console.error("Error updating record:", err);
            toast.error("An error occurred while updating record");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle record deletion
    const handleDeleteRecord = async () => {
        if (!currentRecord) return false;

        try {
            setIsLoading(true);
            const response = await otherTipsService.deleteRecord(currentRecord.id);

            if (response.success) {
                toast.success("Record deleted successfully");
                // Refresh data after deletion
                await Promise.all([
                    fetchBalanceData(),
                    fetchRecords(dateRange.start, dateRange.end)
                ]);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                toast.error(response.error || "Failed to delete record");
                return false;
            }
        } catch (err) {
            console.error("Error deleting record:", err);
            toast.error("An error occurred while deleting record");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // Filter records based on date range and search term
    const filteredRecords = useMemo(() => {
        if (!Array.isArray(records)) return [];

        return records.filter(record => {
            const inDateRange = isDateTimeInRange(record.date, dateRange.start, dateRange.end);

            const matchesSearch =
                !searchTerm ||
                (record.employee && record.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (record.added_by && record.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

            return inDateRange && matchesSearch;
        });
    }, [records, dateRange, searchTerm]);

    // Calculate employee totals from filtered records
    const employeeTotals = useMemo(() => {
        if (!Array.isArray(filteredRecords)) return [];

        const totalsMap = {};

        filteredRecords.forEach(record => {
            const employee = record.employee;
            if (!totalsMap[employee]) {
                totalsMap[employee] = {
                    employee,
                    tipsReceived: 0,
                    tax: 0
                };
            }

            totalsMap[employee].tipsReceived += parseFloat(record.tip_out_cash || 0);
            totalsMap[employee].tax += parseFloat(record.tax || 0);
        });

        return Object.values(totalsMap).sort((a, b) => a.employee.localeCompare(b.employee));
    }, [filteredRecords]);

    // Format date range for display
    const formatDateRange = useCallback(() => {
        return `${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")}`;
    }, [dateRange]);

    // Calculate period summary
    const periodSummary = useMemo(() => {
        if (!filteredRecords) {
            return { tipsReceived: 0, tax: 0 };
        }

        return filteredRecords.reduce(
            (acc, record) => {
                return {
                    tipsReceived: acc.tipsReceived + parseFloat(record.tip_out_cash || 0),
                    tax: acc.tax + parseFloat(record.tax || 0),
                };
            },
            { tipsReceived: 0, tax: 0 }
        );
    }, [filteredRecords]);

    // Handle edit record
    const handleEditRecord = (record) => {
        setCurrentRecord(record);
        setShowEditModal(true);
    };

    // Handle delete record
    const handleDeleteRecordClick = (record) => {
        setCurrentRecord(record);
        setShowDeleteModal(true);
    };


    const handleDateRangeApply = (startDate, endDate) => {
        setDateRange({
            start: startOfDay(startDate),
            end: endOfDay(endDate)
        });
        setIsDatePickerOpen(false);
        setCurrentPage(1);
    };

    const formatCurrency = useCallback((value) => {
        return parseFloat(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }, []);

    const summaryValues = useMemo(() => {
        if (!Array.isArray(filteredRecords)) {
            return {
                tipPool: 0,
                collectedFees: 0,
                addedTokes: 0
            };
        }

        return filteredRecords.reduce(
            (acc, record) => {
                const tipOutCash = parseFloat(record.tip_out_cash || 0);
                const commission = parseFloat(record.tax || 0);

                return {
                    tipPool: acc.tipPool + tipOutCash,
                    collectedFees: acc.collectedFees + (tipOutCash - commission),
                    addedTokes: acc.addedTokes + commission
                };
            },
            { tipPool: 0, collectedFees: 0, addedTokes: 0 }
        );
    }, [filteredRecords]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="text-red-500 text-xl">{error}</div>
            </div>
        );
    }


    const exportOtherTipsRecordsToPDF = () => {
        try {
            const doc = new jsPDF();

            // Set document properties
            doc.setProperties({
                title: 'Other Tips Report',
                subject: 'Other Tips Transactions',
                author: 'Elite Poker Lounge',
                creator: 'Other Tips System'
            });

            // Add logo (if you have base64Logo available)
            if (typeof base64Logo !== 'undefined') {
                const logoWidth = 40;
                const logoHeight = 20;
                doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight);
            }

            // Add timestamp
            const now = new Date();
            const dateTime = now.toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Date & Time: ${dateTime}`, 14, 15);
            doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

            // Title
            doc.setFontSize(18);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Other Tips Records', 14, 40);

            // Summary section
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Balance Summary:', 14, 50);

            doc.setFont(undefined, 'normal');
            doc.text(`Tip Pool: $${formatCurrency(balanceData.tipPool)}`, 14, 60);
            doc.text(`Paid Out: $${formatCurrency(balanceData.collectedFees)}`, 14, 70);
            doc.text(`Added Tokes: $${formatCurrency(balanceData.addedTokes)}`, 14, 80);

            // Period summary section
            doc.setFont(undefined, 'bold');
            doc.text('Period Summary:', 14, 95);

            doc.setFont(undefined, 'normal');
            doc.text(`Total Tips Received: $${formatCurrency(periodSummary.tipsReceived)}`, 14, 105);
            doc.text(`Total Tax: $${formatCurrency(periodSummary.tax)}`, 14, 115);

            // Employee Totals Section
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Employee Totals:', 14, 130);

            // Employee totals table
            const employeeHeaders = ['Employee', 'Tips Received', 'Tax', 'Paid Out'];
            const employeeData = employeeTotals.map(employee => [
                employee.employee,
                `$${formatCurrency(employee.tipsReceived)}`,
                `$${formatCurrency(employee.tax)}`,
                `$${formatCurrency(employee.tipsReceived - employee.tax)}`
            ]);

            // Add employee totals row
            employeeData.push([
                'TOTAL',
                `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tipsReceived, 0))}`,
                `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tax, 0))}`,
                `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + (emp.tipsReceived - emp.tax), 0))}`
            ]);

            let currentY = 135;

            if (employeeTotals.length > 0) {
                autoTable(doc, {
                    head: [employeeHeaders],
                    body: employeeData,
                    startY: currentY,
                    theme: 'plain',
                    styles: {
                        fontSize: 9,
                        cellPadding: 3,
                        textColor: [0, 0, 0],
                        lineColor: [0, 0, 0],
                        lineWidth: 0.1
                    },
                    headStyles: {
                        fillColor: false,
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    bodyStyles: {
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { cellWidth: 60 },
                        1: { cellWidth: 50, halign: 'right' },
                        2: { cellWidth: 50, halign: 'right' }
                    },
                    margin: { left: 14, right: 14 },
                    tableLineColor: [0, 0, 0],
                    tableLineWidth: 0.1,
                    didParseCell: function (data) {
                        // Make the total row bold
                        if (data.row.index === employeeData.length - 1) {
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                });

                currentY = doc.lastAutoTable.finalY + 15;
            }

            // Transaction Details Section
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Transaction Details:', 14, currentY);

            // Prepare transaction table data
            const transactionHeaders = [
                'Date',
                'Employee',
                'Tip Out Cash',
                'Tax',
                'Paid Out',
                'Added By'
            ];

            const transactionData = filteredRecords.map(record => [
                parseAppDateTime(record.date)?.format("MM/DD/YYYY hh:mm A") ?? formatInAppTz(record.date),
                record.employee || 'N/A',
                `$${formatCurrency(record.tip_out_cash)}`,
                `$${formatCurrency(record.tax)}`,
                `$${formatCurrency(parseFloat(record.tip_out_cash || 0) - parseFloat(record.tax || 0))}`,
                record.added_by || 'N/A'
            ]);

            // Add transaction details table
            autoTable(doc, {
                head: [transactionHeaders],
                body: transactionData,
                startY: currentY + 5,
                theme: 'plain',
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1
                },
                headStyles: {
                    fillColor: false,
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    lineWidth: 0.1,
                    lineColor: [0, 0, 0]
                },
                columnStyles: {
                    0: { cellWidth: 40 },
                    1: { cellWidth: 35 },
                    2: { cellWidth: 30, halign: 'right' },
                    3: { cellWidth: 30, halign: 'right' },
                    4: { cellWidth: 35 }
                },
                margin: { left: 14, right: 14 },
                tableLineColor: [0, 0, 0],
                tableLineWidth: 0.1
            });

            // Save the PDF
            const fileName = `Other_Tips_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
            doc.save(fileName);

            toast.success('PDF exported successfully!');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF. Please try again.');
        }
    };

    return (
        <div className="bg-gray-50 p-6">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800">Other Tips Records</h1>
                <p className="text-gray-600 mt-2">Manage and track tip distributions</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                            Tip Pool
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            {isLoading ? (
                                <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
                            ) : (
                                <div className="text-2xl font-bold text-red-600">
                                    ${formatCurrency(summaryValues.tipPool)}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                            Added Tokes
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            {isLoading ? (
                                <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
                            ) : (
                                <div className="text-2xl font-bold text-blue-600">
                                    ${formatCurrency(summaryValues.addedTokes)}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                            Paid Out
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="text-center">
                            {isLoading ? (
                                <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
                            ) : (
                                <div className="text-2xl font-bold text-green-600">
                                    ${formatCurrency(summaryValues.collectedFees)}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>


            {/* Add Record Button */}
            <div className="flex justify-end mb-6">
                <Button
                    onClick={() => setShowAddModal(true)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    disabled={isLoading}
                >
                    <Plus className="mr-2 h-4 w-4" /> Add Record
                </Button>
            </div>

            {/* Filter Controls */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <Button
                            variant="outline"
                            onClick={() => setIsDatePickerOpen(true)}
                            className="w-full justify-start text-left font-normal"
                        >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formatDateRange()}
                        </Button>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by employee or added by..."
                        />
                    </div>
                </div>
            </div>

            {/* Records Table */}
            <Card className="bg-white shadow rounded-lg mb-8">
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <CardTitle className="text-lg font-semibold text-gray-800">
                            Transaction History ({filteredRecords.length} records)
                        </CardTitle>
                        <Button
                            onClick={exportOtherTipsRecordsToPDF}
                            variant="outline"
                            className="bg-yellow-400 hover:bg-yellow-500 text-black"
                        >
                            Export to PDF
                        </Button>
                    </div>
                </CardHeader>

                <CardContent>
                    <OtherTipsTransactionTable
                        records={filteredRecords}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        entriesPerPage={entriesPerPage}
                        setEntriesPerPage={setEntriesPerPage}
                        onEdit={handleEditRecord}
                        onDelete={handleDeleteRecordClick}
                        currentUser={currentUser}
                        dateRange={dateRange}
                        employeeApiService={employeeManagementService}
                        employees={allemployees}
                    />
                </CardContent>
            </Card>

            {/* Employee Totals Section */}
            <Card className="bg-white shadow rounded-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-gray-800">
                        Overall Total Tips Received ({formatDateRange()})
                    </CardTitle>
                    <Button
                        onClick={() => fetchRecords()}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                    >
                        {isLoading ? "Refreshing..." : "Refresh"}
                    </Button>
                </CardHeader>
                <CardContent>
                    <EmployeeTotalsOthersTable
                    filteredRecords={filteredRecords}
                    dateRange={dateRange}
                    employeeApiService={employeeManagementService}
                    employees={allemployees}
                    />
                </CardContent>
            </Card>

            {/* TODO:SHUBHAM TRIBHUVAN */}
            {/* Period Summary */}
            {/* <Card className="bg-white shadow rounded-lg mt-8">
                <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-800">
                        Period Summary ({formatDateRange()})
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h3 className="text-md font-medium text-gray-700">Total Tips Received</h3>
                        <p className="text-2xl font-bold text-green-600">
                            ${formatCurrency(periodSummary.tipsReceived)}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-md font-medium text-gray-700">Total Owe to Toast</h3>
                        <p className="text-2xl font-bold text-blue-600">
                            ${formatCurrency(periodSummary.oweToToast)}
                        </p>
                    </div>
                </CardContent>
            </Card> */}

            {/* Modals */}
            <OtherTipsAddModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onAdd={handleAddRecord}
                employees={allemployees}
                calculateCommission={calculateCommission}
            />

            <OtherTipsEditModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                record={currentRecord}
                onUpdate={handleUpdateRecord}
                employees={allemployees}
                currentUser={currentUser}
            />

            <OtherTipsDeleteModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                record={currentRecord}
                onDelete={handleDeleteRecord}
            />

            {/* Date Range Picker */}
            <DateRangePicker
                isOpen={isDatePickerOpen}
                onClose={() => setIsDatePickerOpen(false)}
                onApply={handleDateRangeApply}
                initialStartDate={dateRange.start}
                initialEndDate={dateRange.end}
            />
        </div>
    );
}